import { BehaviorSubject, map, Observable } from 'rxjs';

import { DriveFileBrowseState, FileItem } from '../models';

const initialState: DriveFileBrowseState = {
  root: null,
  breadcrumb: [],
  items: [],
  loading: false,
  loaded: false,
};

/**
 * Shared state and navigation logic for file browser services.
 * Subclasses provide the data-source specific `setRoot` and `loadItems`.
 */
export abstract class FileBrowserBaseService {
  protected readonly _state: BehaviorSubject<DriveFileBrowseState> = new BehaviorSubject<DriveFileBrowseState>({ ...initialState });

  /**
   * Breadcrumb getter
   * @returns the list of browsed folders
   */
  public getBreadcrumb$: Observable<FileItem[]> = this._state.pipe(
    map((state: DriveFileBrowseState) => state.root ? [state.root, ...state.breadcrumb] : state.breadcrumb),
  );

  /**
   * Items getter
   * @returns the list of items for the current folder
   */
  public getItems$: Observable<FileItem[]> = this._state.pipe(
    map((state: DriveFileBrowseState) => state.items),
  );

  /**
   * Loading state for the items list
   * @returns true if the list is loading
   */
  public isLoading$: Observable<boolean> = this._state.pipe(
    map((state: DriveFileBrowseState) => state.loading),
  );

  /**
   * Loaded state for the items list
   * @returns true if the list has loaded once, without errors
   */
  public isLoaded$: Observable<boolean> = this._state.pipe(
    map((state: DriveFileBrowseState) => state.loaded),
  );

  /**
   * Set the root directory in state. It will appear in first item of breadcrumb with the "root" property to true.
   * @param {{ name: string, id?: string }} root - definition of the root. The name is required, the id is optional
   * @returns {void}
   */
  public abstract setRoot(root: { name: string, id?: string }): void;

  /**
   * Load items for a specific folder or root.
   * It will toggle the loading during the call
   * @param {FileItem} [parent = null] - will load items from this folder. Keep it empty to load from root
   * @return {Observable<void>} nothing if succeeded, throw error if the call fails
   */
  public abstract loadItems(parent?: FileItem | null): Observable<void>;

  /**
   * Recalculate the breadcrumb with navigation to folder
   * @param {{ folder: FileItem }} folder to navigate
   * @return {void}
   */
  public navigateToItem({ folder }: { folder: FileItem }): void {
    const state: DriveFileBrowseState = this._state.value;

    if (folder.root) {
      this.clearNavigation();

      return;
    }

    const folderIndex: number = state.breadcrumb.findIndex(f => f.id === folder.id);
    const breadcrumb: FileItem[] = folderIndex === -1
      // if not existing in list, add it in the end
      ? [...state.breadcrumb, folder]
      // else, cut after found index (we navigate throw the breadcrumb)
      : state.breadcrumb.slice(0, folderIndex + 1);

    this._state.next({ ...state, breadcrumb });
  }

  /**
   * Go back to the previous folder of breadcrumb
   * @return {FileItem} the previous folder, which is now the current folder
   */
  public goBack(): FileItem | null | undefined {
    const state: DriveFileBrowseState = this._state.value;
    const breadcrumb: FileItem[] = state.breadcrumb;

    if (!breadcrumb.length) {
      this.resetState();

      return undefined;
    }

    if (breadcrumb.length === 1) {
      this.clearNavigation();

      return state.root;
    }

    const previous: number = Math.max(breadcrumb.length - 2, 0);
    this.navigateToItem({ folder: breadcrumb[previous] });

    return breadcrumb[previous];
  }

  /**
   * Clear the breadcrumb. Use it when navigate to root
   * @return {void}
   */
  public clearNavigation(): void {
    this._state.next({ ...this._state.value, breadcrumb: [] });
  }

  /**
   * Reset the entire state. Use it when closing the navigation
   * @returns {void}
   */
  public resetState(): void {
    this._state.next({ ...initialState });
  }

  /**
   * @protected
   * Set the loaded list in state.
   * Toggle the loading and loaded properties
   * @param {FileItem[]} items to set in state
   * @return {void}
   */
  protected loadItemsSuccess(items: FileItem[]): void {
    this._state.next({ ...this._state.value, loading: false, loaded: true, items });
  }

  /**
   * @protected
   * Toggle the loading and loaded properties when load fails
   * @return {void}
   */
  protected loadItemsFail(): void {
    this._state.next({ ...this._state.value, loading: false, loaded: false });
  }
}
